import { Box, Link, Typography, useTheme, Avatar, Stack } from "@mui/material";
import BoxCard from "../../shared/components/BoxCard";
import { api, Match, Game } from "../../shared/api/client";
import { InternalLink } from "../../shared/components/InternalLink";
import { ReactNode, useState, useEffect } from "react";
import useUserId from "../../shared/hooks/useUserId";

function relativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function MatchDetails({
  children,
  highlight = false,
}: {
  highlight?: boolean;
  children: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Typography
      variant="subtitle2"
      sx={{
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: highlight
          ? theme.palette.info.main
          : theme.palette.text.secondary,
      }}
    >
      {children}
    </Typography>
  );
}

function MatchLink({
  match,
  hideGameLink,
  boxArtUrl,
}: {
  match: Match;
  hideGameLink?: boolean;
  boxArtUrl?: string;
}) {
  const theme = useTheme();
  const [uid] = useUserId();
  const seatsLeft = match.numPlayers - Object.keys(match.players).length;
  const createdAt = new Date(match.createdAt);

  // Sort players: current user first, then others
  // Use Object.entries to get [playerId, player] pairs since the key is the user ID
  const allPlayers = Object.entries(match.players);
  const sortedPlayers = uid
    ? [...allPlayers].sort(([keyA], [keyB]) => {
        if (keyA === uid) return -1;
        if (keyB === uid) return 1;
        return 0;
      })
    : allPlayers;

  // Link destination for match
  const matchLink = match.status === "active"
    ? `/player/${match.id}?game=${match.gameId}&version=${match.majorVersion}`
    : `/match/${match.id}`;
  const isExternalLink = match.status === "active";

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
      {/* Box Art Thumbnail - clickable */}
      {isExternalLink ? (
        <Link href={matchLink} sx={{ textDecoration: "none", flexShrink: 0 }}>
          <Avatar
            variant="rounded"
            src={boxArtUrl}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.grey[700],
              fontSize: "0.75rem",
            }}
          >
            {match.gameName.charAt(0)}
          </Avatar>
        </Link>
      ) : (
        <InternalLink to={matchLink} style={{ textDecoration: "none", flexShrink: 0 }}>
          <Avatar
            variant="rounded"
            src={boxArtUrl}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.grey[700],
              fontSize: "0.75rem",
            }}
          >
            {match.gameName.charAt(0)}
          </Avatar>
        </InternalLink>
      )}

      {/* Match Info */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* Title row: match name, game name, timestamp */}
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Typography fontWeight="medium" noWrap component="span">
            {isExternalLink ? (
              <Link
                href={matchLink}
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {match.name}
              </Link>
            ) : (
              <InternalLink to={matchLink}>{match.name}</InternalLink>
            )}
          </Typography>
          {!hideGameLink && (
            <Box
              component={InternalLink}
              to={`/game/${match.gameId}`}
              sx={{ textDecoration: "none", color: "inherit", "&:hover": { textDecoration: "underline" } }}
            >
              <Typography variant="body2" color="text.secondary" component="span">
                {match.gameName}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" component="span">
            ·{" "}
          </Typography>
          <Box
            component={InternalLink}
            to={`/match/${match.id}`}
            sx={{ textDecoration: "none", color: "inherit", "&:hover": { textDecoration: "underline" } }}
          >
            <Typography variant="body2" color="text.secondary" component="span">
              {relativeTime(createdAt)}
            </Typography>
          </Box>
          {match.discordThreadId && match.discordGuildId && (
            <>
              <Typography variant="body2" color="text.secondary" component="span">
                ·{" "}
              </Typography>
              <Link
                href={`https://discord.com/channels/${match.discordGuildId}/${match.discordThreadId}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textDecoration: "none",
                  "&:hover": { color: "info.main", textDecoration: "underline" },
                }}
              >
                #thread
              </Link>
            </>
          )}
        </Stack>

        {/* Status line for pending */}
        {match.status === "pending" && (
          <MatchDetails>
            Seeking {seatsLeft} {seatsLeft > 1 ? "players" : "player"}
          </MatchDetails>
        )}

        {/* Players list - bold = their turn, yellow = your turn */}
        <Typography
          variant="subtitle2"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: theme.palette.text.secondary,
          }}
        >
          {sortedPlayers.map(([playerId, p], i) => {
            const isTheirTurn = !!p.prompt; // truthy check - has non-empty prompt
            const isMe = playerId === uid;
            const isMyTurn = isMe && isTheirTurn;
            const scoreStr = p.score !== undefined ? ` (${p.score})` : "";
            return (
              <span key={playerId}>
                {i > 0 && ", "}
                <span style={{
                  fontWeight: isTheirTurn ? 600 : 400,
                  color: isMyTurn ? theme.palette.info.main : undefined,
                }}>
                  {p.name}{scoreStr}
                </span>
              </span>
            );
          })}
        </Typography>
      </Box>
    </Stack>
  );
}

export function MatchListCard({
  label,
  status,
  playerId,
  gameId,
  hideGameLink,
}: {
  label: string;
  status?: string;
  playerId?: string;
  gameId?: string;
  hideGameLink?: boolean;
}) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [games, setGames] = useState<Record<string, Game>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getMatches({ status, playerId, gameId })
      .then((data) => {
        setMatches(data.matches);
        setLoading(false);

        // Fetch games for box art (unique game IDs only)
        const uniqueGameIds = [...new Set(data.matches.map(m => m.gameId))];
        Promise.all(uniqueGameIds.map(id => api.getGame(id)))
          .then((gameResponses) => {
            const gamesMap: Record<string, Game> = {};
            gameResponses.forEach(({ game }) => {
              gamesMap[game.id] = game;
            });
            setGames(gamesMap);
          })
          .catch(() => {
            // Box art is optional, don't fail the whole component
          });
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [status, playerId, gameId]);

  if (loading) {
    return <BoxCard title="Loading..."> </BoxCard>;
  }
  return (
    <BoxCard title={label}>
      {error && <Typography>Error: {error}</Typography>}
      <Stack spacing={1}>
        {matches.map((match) => (
          <MatchLink
            key={match.id}
            match={match}
            hideGameLink={hideGameLink}
            boxArtUrl={games[match.gameId]?.boxArtUrl}
          />
        ))}
      </Stack>
      {matches.length === 0 && (
        <Typography color="text.secondary">No matches here.</Typography>
      )}
    </BoxCard>
  );
}
